import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import myConfig from "../../configs/config";
import { windFlowDegrees } from "./dashboardWindDirection";
import "./DashboardHourlyForecastBar.css";

const REFRESH_MS = 10 * 60 * 1000;
const HOURS = 24;

export function getFarmLatLng(farm) {
  if (!farm) return null;
  const centroid = farm.centroid || {};
  let lat = Number(centroid.lat);
  let lng = Number(centroid.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    lat = Number(String(farm.latitude != null ? farm.latitude : "").replace(",", "."));
    lng = Number(String(farm.longitude != null ? farm.longitude : "").replace(",", "."));
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    const geo = farm.geo_coords;
    if (Array.isArray(geo) && geo.length >= 2) {
      lng = Number(geo[0]);
      lat = Number(geo[1]);
    } else if (geo && typeof geo === "object") {
      lat = Number(geo.lat != null ? geo.lat : geo.y);
      lng = Number(geo.lng != null ? geo.lng : geo.x);
    }
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function coordsMoved(prev, next) {
  if (!prev || !next) return true;
  return Math.abs(prev.lat - next.lat) >= 0.0008 || Math.abs(prev.lng - next.lng) >= 0.0008;
}

function nearestForecast(list, targetMs) {
  let best = null;
  let bestDiff = Infinity;
  list.forEach((item) => {
    const ms = Number(item.dt) * 1000;
    if (!Number.isFinite(ms)) return;
    const diff = Math.abs(ms - targetMs);
    if (diff < bestDiff) {
      best = item;
      bestDiff = diff;
    }
  });
  return best;
}

function buildHourSlots(list) {
  if (!Array.isArray(list) || !list.length) return [];
  const now = new Date();
  now.setMinutes(0, 0, 0);
  const slots = [];
  for (let hour = 0; hour < HOURS; hour += 1) {
    const at = new Date(now.getTime() + hour * 60 * 60 * 1000);
    const item = nearestForecast(list, at.getTime());
    if (!item) continue;
    const main = item.main || {};
    const wind = item.wind || {};
    const pop = Number(item.pop);
    slots.push({
      key: at.toISOString(),
      hour: at.getHours(),
      temp: Number.isFinite(Number(main.temp)) ? Math.round(main.temp) : null,
      windKmh: Number.isFinite(Number(wind.speed))
        ? Math.round(Number(wind.speed) * 3.6)
        : null,
      windDeg: Number(wind.deg),
      rainChance: Number.isFinite(pop) ? Math.round(pop * 100) : null,
      humidity: Number.isFinite(Number(main.humidity))
        ? Math.round(Number(main.humidity))
        : null,
    });
  }
  return slots;
}

class DashboardHourlyForecastBar extends Component {
  constructor(props) {
    super(props);
    this.state = { slots: [] };
    this.refreshTimer = null;
    this.fetchSeq = 0;
  }

  getQueryCoords() {
    const mapCenter = this.props.mapCenter;
    if (
      mapCenter &&
      Number.isFinite(Number(mapCenter.lat)) &&
      Number.isFinite(Number(mapCenter.lng))
    ) {
      return { lat: Number(mapCenter.lat), lng: Number(mapCenter.lng) };
    }
    return getFarmLatLng(this.props.farm);
  }

  componentDidMount() {
    this.loadForecast();
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.mapCenter || getFarmLatLng(prevProps.farm);
    const next = this.getQueryCoords();
    if (!next) return;
    if (coordsMoved(prev, next)) {
      this.loadForecast();
    }
  }

  componentWillUnmount() {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer);
    }
  }

  loadForecast = async () => {
    const coords = this.getQueryCoords();
    if (!coords) return;
    const fetchSeq = ++this.fetchSeq;
    const url = `${myConfig.WEATHER_DOMAIN_API.replace(
      "/weather?",
      "/forecast?"
    )}&lat=${coords.lat}&lon=${coords.lng}&lang=pt_br`;

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const json = await response.json();
      if (fetchSeq !== this.fetchSeq) return;
      this.setState({ slots: buildHourSlots(json.list) }, () => {
        const current = this.state.slots[0];
        if (this.props.onWindChange && current) {
          this.props.onWindChange(current.windDeg);
        }
      });
      if (this.refreshTimer) {
        window.clearInterval(this.refreshTimer);
      }
      this.refreshTimer = window.setInterval(() => this.loadForecast(), REFRESH_MS);
    } catch (error) {
      // keep last slots
    }
  };

  render() {
    const { t } = this.props;
    const { slots } = this.state;
    if (!slots.length) return null;

    return (
      <div className="dashboard-hourly-forecast" aria-label={t("Previsão das próximas 24 horas")}>
        {slots.map((slot) => (
          <div
            key={slot.key}
            className="dashboard-hourly-forecast__cell"
            title={`${String(slot.hour).padStart(2, "0")}h · ${
              slot.temp != null ? `${slot.temp}°` : "--"
            } · ${slot.windKmh != null ? `${slot.windKmh} km/h` : "--"} · ${
              slot.rainChance != null ? `${slot.rainChance}% ${t("chuva")}` : `-- ${t("chuva")}`
            } · ${
              slot.humidity != null
                ? `${slot.humidity}% ${t("Umidade do ar")}`
                : `-- ${t("Umidade do ar")}`
            }`}
          >
            <span className="dashboard-hourly-forecast__hour">
              {String(slot.hour).padStart(2, "0")}h
            </span>
            <span
              className="dashboard-hourly-forecast__arrow"
              style={{
                transform: `rotate(${windFlowDegrees(slot.windDeg)}deg)`,
              }}
            >
              ↑
            </span>
            <span className="dashboard-hourly-forecast__wind">
              {slot.windKmh != null ? `${slot.windKmh}` : "--"}
            </span>
            <span className="dashboard-hourly-forecast__temp">
              {slot.temp != null ? `${slot.temp}°` : "--"}
            </span>
            <span className="dashboard-hourly-forecast__rain">
              {slot.rainChance != null ? `${slot.rainChance}%` : "--"}
            </span>
            <span className="dashboard-hourly-forecast__humidity">
              {slot.humidity != null ? `${slot.humidity}%` : "--"}
            </span>
          </div>
        ))}
      </div>
    );
  }
}

export default withTranslation()(DashboardHourlyForecastBar);
